// Dashboard Favorites: pure card logic, kept apart from the page for tests.

export const KINDS = ['SC', 'TE', 'LT'];

const UNIT_MINUTES = { s: 1 / 60, m: 1, h: 60, d: 1440, w: 10080 };
const tfMinutes = (tf) => {
  const m = /^(\d+)([smhdw])$/.exec(tf || '');
  return m ? Number(m[1]) * UNIT_MINUTES[m[2]] : Infinity;
};

const rowKey = (r) => `${r.tf}|${r.senior}|${r.kind}|${r.dir}|${r.text}`;

/**
 * One card per broker|symbol (the user's call: stars on several accounts merge).
 * `asset` is the first account's fetch-assets row that matches, null when none
 * does (instrument removed): such a card shows but cannot open.
 */
export const toCards = (favorites, assets) => {
  const cards = new Map();
  favorites.forEach((f) => {
    const key = `${f.broker}|${f.symbol}`;
    const card = cards.get(key) || {
      key,
      broker: f.broker,
      symbol: f.symbol,
      accounts: [],
      rows: [],
      // Every starred timeframe on any account, whatever the filters show.
      starred: [],
      asset: null,
      // Every loaded account row of the instrument, starred there or not: the placement filter reads them.
      holdings: assets.filter((a) => a.broker === f.broker && a.symbol === f.symbol),
    };
    card.accounts.push(f.account);
    card.starred = [...new Set([...card.starred, ...f.timeframes])];
    card.asset = card.asset || assets.find((a) => (
      a.broker === f.broker && a.symbol === f.symbol && (f.broker === 'okx' || a.account === f.account)
    )) || null;
    card.rows.push(...f.rows);
    cards.set(key, card);
  });
  return [...cards.values()].map((card) => {
    const seen = new Set();
    const rows = card.rows.filter((r) => !seen.has(rowKey(r)) && seen.add(rowKey(r)));
    // A bare row only stands in for a star with nothing live on any account.
    const live = new Set(rows.filter((r) => r.kind).map((r) => r.tf));
    return {
      ...card,
      rows: rows
        .filter((r) => r.kind || !live.has(r.tf))
        .sort((a, b) => tfMinutes(a.tf) - tfMinutes(b.tf)),
    };
  });
};

export const isScannerCard = (card) => card.asset?.trading === false;

// Placement tokens: where an instrument sits now.
export const SCANNER = 'scanner';
export const folderToken = (name) => `folder:${name}`;
export const accountToken = (broker, account) => `account:${broker}|${account}`;

// Trading on the account = it holds the instrument with the TRADING flag on.
// An unknown flag counts as trading, as on the Dashboard (shown, not hidden).
// OKX rows carry no account of their own: any OKX account holds them.
const tradesOn = (card, token) => card.holdings.some((a) => (
  a.trading !== false
  && (a.broker === 'okx' ? token.startsWith('account:okx|') : token === accountToken(a.broker, a.account))
));

/**
 * In any of the picked places — OR within placement: "Scanner" is any folder,
 * a folder is that folder, an account is trading there. Empty = no filter.
 */
export const matchesPlacement = (card, placement, folderOf = {}) => !placement.length || placement.some((t) => (
  (t === SCANNER && Boolean(folderOf[card.key]))
  || (folderOf[card.key] && t === folderToken(folderOf[card.key]))
  || (t.startsWith('account:') && tradesOn(card, t))
));

// Filters saved before placement had a "folders" list.
export const migrateFilters = ({ folders, ...rest }) => ({
  ...rest,
  placement: [...(rest.placement || []), ...(folders || []).map(folderToken)],
});

/**
 * Sectype, indicator and placement filters, AND-ed; empty = no filter. The
 * indicator filter also trims the rows, and a card left with none drops out.
 */
export const filterCards = (cards, { sectypes = [], kinds = [], placement = [] }, folderOf = {}) => cards
  .filter((c) => !sectypes.length || sectypes.includes(c.asset?.sectype))
  .filter((c) => matchesPlacement(c, placement, folderOf))
  .map((c) => (kinds.length ? { ...c, rows: c.rows.filter((r) => kinds.includes(r.kind)) } : c))
  .filter((c) => !kinds.length || c.rows.length > 0);

// Every live row names its pair (32m→2h), bound level pair or not: a junior may have several seniors.
export const rowLabel = (r) => (
  [r.senior ? `${r.tf}→${r.senior}` : r.tf, r.kind, r.dir, r.text].filter(Boolean).join(' ') + (r.kind ? '' : ' —')
);

/**
 * Saves that take `timeframes` off a card: the card merges accounts, so every
 * account starring one of them gets its list back without them (an empty list
 * unstars the instrument there). Accounts that never starred them are left alone.
 */
export const unstarPlan = (favorites, broker, symbol, timeframes) => favorites
  .filter((f) => f.broker === broker && f.symbol === symbol && f.timeframes.some((tf) => timeframes.includes(tf)))
  .map((f) => ({
    account: f.account, broker, symbol, timeframes: f.timeframes.filter((tf) => !timeframes.includes(tf)),
  }));

// The (account, broker) pairs whose asset lists the cards need, each once.
export const assetSources = (favorites) => [
  ...new Map(favorites.map((f) => [`${f.broker}|${f.account}`, { account: f.account, broker: f.broker }])).values(),
];
